import _ from "lodash";
import type { Map, Iterable } from "immutable";
import type { InventoryErrors } from "components/product/order/variant/types";
import { IReplenishment, IAllocation, IAvailableInventory, type IFetchOptions } from "types/inventory";
import { getStartOfDate } from "helpers/dates";
import me from "models/me";
import { format } from "lib/globalize-util";
import type { IScope } from "reducers/scope";
import type { ICompany } from "components/contacts/contacts-types";

interface IHeapReplenishment extends IReplenishment {
    oversell?: number; // oversell quantity requested for a replenishment
}

interface ILimits {
    max?: number;
    min?: number;
}

/**
 * Checks if pivot is overordered after allocation of all requests.
 * Soft algorithm that returns overzordering errors only for the pivot if it cannot be allocated at all.
 */
export const isOverordered = (pivot: IAllocation, requests: IAllocation[], replenishments: IReplenishment[]) => {
    const heap = getAllocationHeap(replenishments);
    const related = getRelated(heap, pivot);
    // optimization not to calculate trivial cases
    if (_.sumBy(related, "quantity") < pivot?.quantity) {
        return true;
    }
    _.forEach(_.sortBy(requests, "date"), (request) => request.quantity > 0 && allocate(heap, request, true));
    return _.last(related)?.oversell < 0 || _.sumBy(related, "quantity") < 0; // support for negative inventory
};

export const isOversellHidden = ({ allows_overselling }: { allows_overselling?: boolean }): boolean =>
    allows_overselling && me.isBuyer() && me.hasPermission("can_oversell");

/**
 * Check if an allocation request can be allocated among all requests within the set of replenishments.
 */
export const canAllocateRelated = (pivot: IAllocation, allRequests: IAllocation[], replenishments: IReplenishment[]) =>
    canAllocate(getRelatedRequests(pivot, allRequests, replenishments), replenishments);

/**
 * Check if all number allocation requests can be allocated within the set of replenishments.
 */
export const canAllocate = (requests: IAllocation[], replenishments: IReplenishment[]): boolean => {
    const heap = getAllocationHeap(replenishments);
    return _.every(_.sortBy(requests, "date"), (request) => request.quantity <= 0 || allocate(heap, request) <= 0);
};

/**
 * Collects all related allocation requests - all requests that belong to the same set of replenishments and may cause allocation errors across those replenishments.
 */
export const getRelatedRequests = (pivot: IAllocation, requests: IAllocation[], replenishments: IReplenishment[]) => {
    const related = getRelated(replenishments);
    const pivotSet = related(pivot);
    return _.filter(requests, (x) => x.quantity > 0 && !_.isEmpty(_.intersection(related(x), pivotSet)));
};

// Session key to cache replenishments per session
export const getSessionKey = (options?: IFetchOptions) => `${options?.session}-${options?.forRef}-${options?.fromRef}`;

/**
 * Allocates requests into replenishments.
 * WARNING: this method mutates parameters.
 */
const allocate = (heap: IHeapReplenishment[], request: IAllocation, full?: boolean): number => {
    const related = getRelated(heap, request);
    let reminder = request.quantity;
    _.forEach(related, ({ quantity }, idx) => {
        if (quantity >= reminder) {
            related[idx].quantity -= reminder;
            reminder = 0;
            return false; // break the loop
        } else if (quantity > 0) {
            reminder -= quantity;
            related[idx].quantity = 0;
        }
    });

    if (full && !_.isEmpty(related) && reminder > 0) {
        _.last(related).oversell = (_.last(related).oversell || 0) - reminder;
    }
    return reminder;
};

/**
 * Generates the Inventory Arriving Table with quantities for each available date and SKU.
 * Returns a sorted list of rows with
 * - arriving date (period state for one of replenishments),
 * - arriving and total inventory values generated according to visibility limits,
 * - map of SKU represents columns with arriving and total inventory and display values generated according to limits.
 */
export const getArrivingTable = (map: { [sku: string]: IReplenishment[] }, limits?: ILimits) => {
    const now = new Date().setUTCHours(0, 0, 0, 0);
    const getDate = (x: number) => Math.max(x, now);
    const dates = _.sortBy(_.uniq(_.flatMap(map, (list) => _.map(list, (x) => getDate(x.period_start))))); // list of arriving dates
    return _.map(dates, (date) => {
        const skus = _.mapValues(map, (replenishments) => {
            const arriving = _.sumBy(
                _.filter(replenishments, (x) => getDate(x.period_start) === date),
                "quantity"
            );
            const total = _.sumBy(getRelated(replenishments, { date }), "quantity");
            return {
                arriving,
                total,
                display: {
                    arriving: getTotal(arriving, limits),
                    total: getTotal(total, limits),
                },
            };
        });
        return {
            date,
            arriving: getTotal(_.map(skus, "arriving"), { min: 0, ...limits }), // existing logic of the total calculation with 0
            total: getTotal(_.map(skus, "total"), { min: 0, ...limits }),
            skus,
        };
    });
};

/**
 * Calculate the total of values with view limits.
 */
export const getTotal = (values: number | number[], limits?: ILimits): string => {
    const list = _.castArray(values);
    const isUpperBounded = limits?.max && _.some(list, (qty) => qty > limits?.max);
    const total = _.sumBy(list, (qty) => _.clamp(qty || 0, limits?.min ?? -Infinity, limits?.max || Infinity));
    return total + (isUpperBounded ? "+" : "");
};

/**
 * Collects available inventory values for each SKU and date.
 */
export const getAvailableInventoryMap = (
    map: { [sku: string]: IReplenishment[] },
    dates: number[],
    brand: IScope["brand"]
): IAvailableInventory => {
    const inventoryMap: IAvailableInventory = {};
    _.forEach(map, (list, sku) => {
        _.forEach(dates, (date) => {
            const available = getAvailableInventory(date, list);
            inventoryMap[`${sku}_${date}`] = {
                available,
                display: applyVisibilityRules(available, brand),
            };
        });
    });
    return inventoryMap;
};

export const getAvailableInventory = (date: number | Date, replenishments: IReplenishment[]): number =>
    _.sumBy(getRelated(replenishments, { date }), "quantity");

export const applyVisibilityRules = function (quantity: number, brand: IScope["brand"]): string {
    const display = me.hasPermission("display_inventory");
    const viewNegative = me.hasPermission("view_negative_inventory");
    const min = brand?.hasPreference("inventory_minimum_threshold");
    const max =
        me.isBuyer() || brand?._brandConnection?.hide_full_inventory
            ? brand?.hasPreference("inventory_maximum_threshold")
            : Infinity;

    if (_.isNil(quantity)) {
        return "";
    }

    const value = !viewNegative && quantity < 0 ? 0 : quantity;

    if (display && max) {
        return value > max ? `${max}+` : _.toString(value);
    }
    if (!display && min && value <= 0) {
        return format("Sold out");
    }
    if (!display && min && value < min) {
        return format("Only |n| left", { n: value });
    }

    return display ? _.toString(value) : "";
};

export const getInventoryErrors = ({
    items,
    inventoryMap,
    skuMap,
    useShipStart,
}: {
    items: Iterable<number, Map<string, any>>;
    inventoryMap: { [sku: string]: IReplenishment[] };
    skuMap: { [sizeName: string]: { _id: string; __size: string } };
    useShipStart: boolean;
}): InventoryErrors => {
    if (!inventoryMap || !skuMap) {
        return null;
    }

    // map of allocation requests grouped by SKU
    const map = _(items.toJS())
        .map((item, index) => ({ item, index })) // collect indices to save error items
        .filter(({ item }) => !item?.deliveryWindow?.prebook && !_.isEmpty(item.quantities))
        .flatMap(({ item, index }) =>
            _.map(
                _.pickBy(item.quantities), // pick only valuable quantities
                (quantity, size) => ({
                    date: getStartOfDate(useShipStart ? item.start : item.end),
                    quantity,
                    skuId: skuMap[size]?._id,
                    item: items.get(index),
                })
            )
        )
        .groupBy("skuId")
        .value();

    const overOrdered: InventoryErrors = {};
    _.forEach(map, (requests, sku) => {
        _.forEach(requests, (pivot: { date: number; quantity: number; item: Map<string, any> }) => {
            if (isOverordered(pivot, requests, inventoryMap[sku])) {
                overOrdered[`${sku}_${pivot.date}`] ||= [];
                overOrdered[`${sku}_${pivot.date}`].push(pivot.item);
            }
        });
    });

    return _.mapValues(overOrdered, _.uniq);
};

export const getCompanyWarehouses = (company: ICompany, scope: IScope) =>
    company
        ? _.uniq(_.compact([company.warehouse, ..._.map(company.addresses, "warehouse")]))
        : scope.brand._brandConnection.assigned_warehouses || _.map(scope.brand.settings.warehouses, "id");

// returns related replenishments
const getRelated = _.curry(
    (replenishments: IHeapReplenishment[], request: Pick<IAllocation, "date">): IHeapReplenishment[] =>
        _.filter(replenishments, ({ period_start, period_end }) =>
            inRange(time(request?.date), period_start, period_end)
        )
);

const getAllocationHeap = (list: IReplenishment[]): IHeapReplenishment[] =>
    _.sortBy<IReplenishment>(_.cloneDeep(_.reject(list, "prebook")), [(x) => x.period_start || 0, "period_end"]);

const inRange = (x: number, start?: number, end?: number) => (!start || start <= x) && (!end || x <= end);
const time = (date: Date | number) => new Date(date).valueOf();
